/* eslint-disable @typescript-eslint/no-explicit-any */
import type { JssStyle, Styles } from 'jss';
import { TBreakpointNames, mediaQueryMin } from './mediaQuery';
import { FULL_TEXT_STYLES } from '../variables';

export const BREAKPOINTS = ['base', 'xs', 'sm', 'md', 'lg', 'xl'] as const;
export type BreakpointKey = (typeof BREAKPOINTS)[number];
export type BreakpointValues<T> = {
  [key in BreakpointKey]?: T;
} & {
  base: T;
};

// string is needed in order to pass and parse objects via prop decorator
export type BreakpointCustomizable<T> = T | BreakpointValues<T> | string;
export type BreakpointValue = string | number | boolean;

export const parseJSON = (
  prop: BreakpointCustomizable<BreakpointValue>
): BreakpointValues<BreakpointValue> | BreakpointValue => {
  if (typeof prop === 'string') {
    try {
      // prop is potentially JSON parsable string, e.g. "{ base: 'block', l: 'inline' }" or "true" or "false"
      return JSON.parse(
        prop
          .replace(/'/g, '"') // convert single quotes to double quotes
          .replace(/[\s"]?([a-z]+)[\s"]?:([^//])/g, '"$1":$2') // wrap keys in double quotes if they don't have them but ignore potential urls
      );
    } catch {
      // prop is string, e.g. "block" or "inline"
      return prop;
    }
  } else {
    // prop is object, e.g. { base: 'block', l: 'inline' } or number, e.g. 123 or boolean, e.g. true
    return prop;
  }
};

// do we need that ?
export const addImportantToRule = (value: any): string => `${value} !important`;

// do we need that ?
export const addImportantToEachRule = (input: JssStyle): JssStyle => {
  return Object.entries(input).reduce(
    (result, [key, value]) =>
      value === null
        ? result
        : ((result[key] =
            typeof value === 'object'
              ? addImportantToEachRule(value as JssStyle)
              : addImportantToRule(value)),
          result),
    {} as JssStyle
  );
};

export type GetJssStyleFunction = (value?: any) => JssStyle;
export type GetJssStyleFunctionWithProp = (value: any, ...args: any[]) => JssStyle;

export const buildResponsiveStyles = <T>(
  rawValue: BreakpointCustomizable<T>,
  getJssStyle: GetJssStyleFunction
): Styles => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const value = parseJSON(rawValue as any);

  const returnValue =
    typeof value === 'object'
      ? Object.keys(value)
          // base styles are applied on root object, responsive styles are nested within
          // hence it is used as the initial object within reduce function
          .filter((key) => key !== 'base')
          .reduce(
            (result, breakpointValue: TBreakpointNames) => ({
              ...result,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              [mediaQueryMin(breakpointValue as any)]: getJssStyle(
                value[breakpointValue]
              ) as Styles,
            }),
            getJssStyle(value.base) as Styles
          )
      : (getJssStyle(value) as Styles);

  return returnValue;
};

export const buildResponsiveStylesWithProps = <T>(
  rawValue: BreakpointCustomizable<T>,
  getJssStyle: GetJssStyleFunctionWithProp,
  extraProp: string,
  ...args: any[]
): Styles => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
  const value = parseJSON(rawValue as any);

  const returnValue =
    typeof value === 'object'
      ? Object.keys(value)
          // base styles are applied on root object, responsive styles are nested within
          // hence it is used as the initial object within reduce function
          .filter((key) => key !== 'base')
          .reduce(
            (result, breakpointValue: TBreakpointNames) => ({
              ...result,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
              [mediaQueryMin(breakpointValue as any)]: getJssStyle(
                value[breakpointValue],
                extraProp,
                ...args
              ) as Styles,
            }),
            getJssStyle(value.base, extraProp, ...args) as Styles
          )
      : (getJssStyle(value, extraProp, ...args) as Styles);
  return returnValue;
};

export const buildResponsiveTextStyles = (styleName: string) => {
  const fontObject = FULL_TEXT_STYLES[styleName];
  if (!fontObject) {
    throw new Error(`Text style ${styleName} does not exist`);
  }

  let styles = {};

  // itterate over fontObject
  Object.keys(fontObject).forEach((key) => {
    // check if key is 'base'
    if (key === 'base') {
      styles = {
        ...styles,
        ...fontObject[key],
      };
      return;
    }

    // get media query from object key
    const mediaQuery = mediaQueryMin(key as any);

    // add styles from fontObject to mediaQuery
    styles = {
      ...styles,
      [mediaQuery]: {
        ...fontObject[key],
      },
    };
  });

  return styles;
};
